import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getKeycloakUserInfo } from "@/services/keycloak";
import { getActiveOrganizationId } from "@/utils/requestScope";
// organization-service.js is plain JS, so this import is implicitly typed `any`.
import { organizationApi } from "../../../services/organization-service";
import { useOrganization } from "../../../contexts/OrganizationContext";
import { useOrganizations } from "./useOrganizations";
import { useMCPQueryScope, mcpRequestOptions } from "./useMCPQueryScope";
import { mcpQueryKeys } from "../query-keys";

export type OrgUser = {
  id: string;
  username: string;
  email?: string;
  organizationRole?: string;
};

export const USERS_QUERY_KEY = ["mcps-org-members"] as const;

// Members are refetched whenever a consumer mounts (opening the connector
// dialog, expanding the drawer's access list): caching them meant a teammate
// added to the org since the page loaded stayed missing from the picker until
// a full reload. The short staleness window only dedupes the burst of mounts
// a single open causes — it never serves a stale list to a new open.
const REFETCH_WINDOW = 30 * 1000;

type OrgMemberDTO = {
  // Membership id — NOT the user id; skip when picking sharing targets.
  id?: string;
  user_id?: string;
  role_id?: string;
  role?: string | { id?: string; name?: string };
  user?: {
    id?: string;
    email?: string;
    username?: string;
    first_name?: string;
    last_name?: string;
    display_name?: string;
  };
};

const buildDisplayName = (raw: OrgMemberDTO): string => {
  const fullName = [raw.user?.first_name, raw.user?.last_name]
    .filter((part): part is string => Boolean(part && part.trim()))
    .join(" ")
    .trim();
  if (fullName) return fullName;
  return raw.user?.display_name ?? raw.user?.username ?? "";
};

const normalizeMember = (raw: OrgMemberDTO): OrgUser | null => {
  // Sharing references the actual user id, not the membership row id.
  const id = raw.user?.id ?? raw.user_id;
  if (!id) return null;
  const username = buildDisplayName(raw);
  if (!username) return null;
  const organizationRole =
    raw.role_id ??
    (typeof raw.role === "string"
      ? raw.role
      : raw.role?.id ?? raw.role?.name);
  return { id, username, email: raw.user?.email, organizationRole };
};

type Options = {
  enabled?: boolean;
};

// Resolve an org id without assuming the org context is populated. In a
// personal workspace nothing is saved to localStorage and DashboardLayout
// never calls loadOrganizations, so we fall back to the fetched org list and
// take the first one (the personal org).
export const useUsers = ({ enabled = true }: Options = {}) => {
  const scope = useMCPQueryScope();
  const client = useQueryClient();
  const { currentOrganization, organizations } = useOrganization();
  const contextOrgId = currentOrganization?.id ?? organizations?.[0]?.id;

  // Shares the organizations query with the connector dialog, so the members
  // lookup reuses that response instead of issuing its own org request.
  const organizationsQuery = useOrganizations({ enabled });
  const fetchedOrgs = organizationsQuery.data ?? [];
  const orgId =
    contextOrgId ??
    localStorage.getItem("currentOrganizationId") ??
    fetchedOrgs[0]?.id;

  const queryKey = [...mcpQueryKeys.scope(scope.key), "members", orgId ?? null];
  const query = useQuery<OrgUser[]>({
    queryKey,
    notifyOnChangeProps: "all",
    enabled: enabled && scope.enabled && Boolean(orgId),
    staleTime: REFETCH_WINDOW,
    refetchOnMount: "always",
    queryFn: async ({ signal }) => {
      const response = await organizationApi.getOrganizationMembers(orgId, mcpRequestOptions(scope, signal));
      const raw = (response?.data ?? []) as OrgMemberDTO[];
      if (!Array.isArray(raw)) return [];
      const users = raw
        .map(normalizeMember)
        .filter((u): u is OrgUser => u !== null);
      return users.sort((a, b) => a.username.localeCompare(b.username));
    },
  });
  const isCurrent = () => enabled && scope.enabled && scope.active &&
    !scope.controller.signal.aborted && getKeycloakUserInfo()?.id === scope.key.principalId &&
    getActiveOrganizationId() === scope.requestScope.organizationId;
  // Re-read the canonical query at interaction time: observer delivery/effects
  // must not create a window where invalidated or old-scope data can be used.
  const validateData = () => {
    const state = client.getQueryState<OrgUser[]>(queryKey);
    return isCurrent() && Boolean(orgId) && state?.status === "success" &&
      state.fetchStatus === "idle" && !state.isInvalidated && Array.isArray(state.data);
  };
  const resolutionError = !orgId && (organizationsQuery.isError || organizationsQuery.isSuccess);
  const retry = () => {
    if (!isCurrent()) return;
    // cancelRefetch:false shares an existing request, including rapid retries.
    if (!orgId) return organizationsQuery.refetch({ cancelRefetch: false });
    return query.refetch({ cancelRefetch: false });
  };
  return {
    ...query,
    isError: query.isError || resolutionError,
    isPending: query.isPending && !resolutionError,
    isFetching: query.isFetching || (!orgId && organizationsQuery.isFetching),
    canUseData: validateData(),
    validateData,
    retry,
  };
};
